import { QueryClient, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { findAPIListQueryItem } from 'shared/api/APIListQuery';
import { APIMutationOptions, useAPIMutation } from 'shared/api/APIMutation';
import {
  APIQueryDataUpdater,
  APIQueryResult,
  setAPIQueryData,
  useAPIQuery,
  UseAPIQueryOptions,
} from 'shared/api/APIQuery';
import { APIResponse, requestCarrierAPI } from 'shared/api/CarrierAPIClient';
import { requestCarrierAPIPublic } from 'shared/api/PublicCarrierAPIClient';
import {
  DeactivationDriverDTO,
  DriverActivateDTO,
  DriverActivationDetailsDTO,
  driverActivationDetailsSchema,
  DriverDTO,
  DriverEditLegacyDTO,
  DriverInvitationDTO,
  driverInvitationSchema,
  driverSchema,
  InviteDriverDTO,
} from 'shared/modules/driver/DriverDTO';
import { useInvalidateSubscriptionQueries } from 'shared/modules/subscription/SubscriptionAPI';
import { shallowDiff } from 'shared/utils/DataUtils';

function setDriverQueryData(
  queryClient: QueryClient,
  guid: string,
  updater: APIQueryDataUpdater<DriverDTO>,
): void {
  setAPIQueryData(queryClient, ['drivers', 'item', { guid }], updater);
}

function useDriversCache() {
  const queryClient = useQueryClient();

  return useMemo(() => {
    function invalidateDrivers() {
      void queryClient.invalidateQueries({ queryKey: ['drivers'] });
    }

    function invalidateDriverActivationDetails(activationCode: string) {
      void queryClient.invalidateQueries({
        queryKey: ['drivers', 'activation-details', { activationCode }],
      });
    }

    return { invalidateDrivers, invalidateDriverActivationDetails };
  }, [queryClient]);
}

export type DriverAcceptInvitationResponse =
  APIResponse<DriverInvitationDTO> & {
    url: string | null;
  };

export function useDriversAPI() {
  const queryClient = useQueryClient();
  const { invalidateDrivers, invalidateDriverActivationDetails } =
    useDriversCache();
  const invalidateSubscriptionDetails = useInvalidateSubscriptionQueries();

  return useMemo(
    () => ({
      createDriver: (
        json: DriverEditLegacyDTO,
      ): Promise<APIResponse<DriverDTO>> =>
        requestCarrierAPI('POST /internal/web/drivers/', { json }).then(
          (response) => {
            invalidateDrivers();
            return { ...response, data: driverSchema.cast(response.data) };
          },
        ),

      patchDriver: (
        guid: string,
        updates: Partial<DriverDTO>,
        original?: DriverDTO,
      ) =>
        requestCarrierAPI(['PATCH /internal/web/drivers/{guid}/', { guid }], {
          json: shallowDiff(updates, original),
        }).then((response) => {
          invalidateDrivers();
          return { ...response, data: driverSchema.cast(response.data) };
        }),

      setPassword: (activationCode: string, password: string) =>
        requestCarrierAPIPublic<
          APIResponse<DriverActivationDetailsDTO> & { url: string | null }
          // Backend sends url outside of data when response code is 302
        >(
          [
            'POST /internal/web/carrier/drivers/{activationCode}/activate/',
            { activationCode },
          ],
          {
            json: { password },
          },
        ).then((response) => {
          invalidateDriverActivationDetails(activationCode);
          return {
            ...response,
            data: driverActivationDetailsSchema.cast(response.data),
          };
        }),

      deactivateDriver: ({ guid, reason }: DeactivationDriverDTO) =>
        requestCarrierAPI(
          ['POST /internal/web/drivers/{guid}/deactivate/', { guid }],
          {
            json: { reason },
          },
        ).then((response) => {
          void queryClient.invalidateQueries({ queryKey: ['drivers'] });

          setDriverQueryData(queryClient, guid, (driver) => ({
            ...driver,
            is_suspended: false,
          }));

          return response;
        }),
      acceptInvite: (invitationCode: string, json: DriverActivateDTO) => {
        return requestCarrierAPIPublic<DriverAcceptInvitationResponse>(
          [
            'POST /internal/web/drivers/invites/{invitationCode}/',
            { invitationCode },
          ],
          { json },
        );
      },

      inviteDriver: (json: InviteDriverDTO): Promise<DriverDTO> =>
        requestCarrierAPI('POST /internal/web/drivers/', { json }).then(
          (res) => {
            void queryClient.invalidateQueries({ queryKey: ['drivers'] });
            void queryClient.invalidateQueries({
              queryKey: ['settings', 'subscription'],
            });
            invalidateSubscriptionDetails();

            return driverSchema.cast(res.data);
          },
        ),
      reinviteDriver: (guid: string) => {
        return requestCarrierAPI([
          'POST /internal/web/drivers/{guid}/invite/',
          { guid },
        ]).then((res) => {
          invalidateSubscriptionDetails();
          void queryClient.invalidateQueries({ queryKey: ['drivers'] });

          return res;
        });
      },
      resendInviteDriver: (guid: string) => {
        return requestCarrierAPI([
          'POST /internal/web/drivers/{guid}/resend-invite/',
          { guid },
        ]).then((res) => {
          invalidateSubscriptionDetails();
          void queryClient.invalidateQueries({ queryKey: ['drivers'] });
          return res;
        });
      },

      acceptJoinRequest: (guid: string, name?: string) => {
        return requestCarrierAPI(
          ['POST /internal/web/drivers/{guid}/accept/', { guid }],
          {
            json: { name },
          },
        ).then((res) => {
          invalidateSubscriptionDetails();
          void queryClient.invalidateQueries({ queryKey: ['drivers'] });

          return res;
        });
      },
      declineJoinRequest: (guid: string) => {
        return requestCarrierAPI([
          'POST /internal/web/drivers/{guid}/decline/',
          { guid },
        ]).then((res) => {
          invalidateSubscriptionDetails();
          void queryClient.invalidateQueries({ queryKey: ['drivers'] });

          return res;
        });
      },

      updateDriverNotes: (guid: string, notes: string) => {
        return requestCarrierAPI(
          ['PUT /internal/web/drivers/{guid}/notes/', { guid }],
          {
            json: { notes },
          },
        ).then((data) => {
          void queryClient.invalidateQueries({ queryKey: ['drivers'] });

          return data;
        });
      },

      acceptExistingDriverJoinRequest: (activationCode: string) => {
        return requestCarrierAPIPublic<DriverAcceptInvitationResponse>([
          'POST /internal/web/drivers/invites/{activationCode}/accept/',
          { activationCode },
        ]);
      },

      declineExistingDriverJoinRequest: (activationCode: string) => {
        return requestCarrierAPIPublic([
          'POST /internal/web/drivers/invites/{activationCode}/decline/',
          { activationCode },
        ]);
      },
    }),
    [
      invalidateDrivers,
      invalidateDriverActivationDetails,
      queryClient,
      invalidateSubscriptionDetails,
    ],
  );
}

export function useDriver(
  guid: string | null | undefined,
  options?: UseAPIQueryOptions<DriverDTO>,
): APIQueryResult<DriverDTO> {
  const queryClient = useQueryClient();
  return useAPIQuery<DriverDTO>(
    ['drivers', 'item', { guid }],
    () => requestCarrierAPI(['GET /internal/web/drivers/{guid}/', { guid }]),
    {
      ...options,
      enabled: !!guid,
      schema: driverSchema,
      initialData() {
        return findAPIListQueryItem(
          ['drivers', 'list'],
          queryClient,
          (driver: DriverDTO) => driver.guid === guid,
        );
      },
    },
  );
}

export function useDriverActivationDetails(
  activationCode: string,
  options?: UseAPIQueryOptions<DriverActivationDetailsDTO>,
): APIQueryResult<DriverActivationDetailsDTO> {
  return useAPIQuery(
    ['drivers', 'activation-details', { activationCode }],
    () =>
      requestCarrierAPIPublic([
        'GET /internal/web/carrier/drivers/{activationCode}/activate/',
        { activationCode },
      ]),
    { ...options, schema: driverActivationDetailsSchema },
  );
}

export function useDriverInvitationDetails(
  invitationCode: string,
  options?: UseAPIQueryOptions<DriverInvitationDTO>,
): APIQueryResult<DriverInvitationDTO> {
  return useAPIQuery(
    ['drivers', 'invitation-details', { invitationCode }],
    () =>
      requestCarrierAPIPublic([
        'GET /internal/web/drivers/invites/{invitationCode}/',
        { invitationCode },
      ]),
    { ...options, schema: driverInvitationSchema },
  );
}

export function useDriversUndelieveredLoadsCount(
  guid?: string,
  options?: UseAPIQueryOptions<{ undelivered: number }>,
): APIQueryResult<{ undelivered: number }> {
  return useAPIQuery(
    ['undelivered-loads', 'count', { guid }],
    () =>
      requestCarrierAPI([
        'GET /internal/web/drivers/{guid}/load-count/',
        { guid },
      ]),
    { ...options },
  );
}

export function useAcceptDriverRequest(
  options?: APIMutationOptions<[guid: string, name?: string]>,
) {
  const { acceptJoinRequest } = useDriversAPI();

  return useAPIMutation(
    ([guid, name]) => acceptJoinRequest(guid, name),
    options,
  );
}

export function useAcceptExistingDriverRequest(
  options?: APIMutationOptions<string>,
) {
  const { acceptExistingDriverJoinRequest } = useDriversAPI();

  return useAPIMutation(
    (activationCode) => acceptExistingDriverJoinRequest(activationCode),
    options,
  );
}

export function useDeclineExistingDriverRequest(
  options?: APIMutationOptions<string>,
) {
  const { declineExistingDriverJoinRequest } = useDriversAPI();

  return useAPIMutation(
    (activationCode) => declineExistingDriverJoinRequest(activationCode),
    options,
  );
}

export function invalidateQuery(
  queryClient: QueryClient,
  invitationCode: string,
  status: 'declined' | 'activated' | 'expired' = 'activated',
) {
  void queryClient.invalidateQueries({
    queryKey: ['settings', 'subscription'],
  });
  void queryClient.invalidateQueries({
    queryKey: ['settings', 'subscription-details'],
  });
  const queryKey = ['drivers', 'invitation-details', { invitationCode }];
  const invitaionDetails =
    queryClient.getQueryData<DriverInvitationDTO>(queryKey);

  void queryClient.setQueryData(queryKey, { ...invitaionDetails, status });

  void queryClient.invalidateQueries({ queryKey: ['drivers'] });
}
